import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {evaluatePassword, type PasswordStrengthResult} from '@/lib/password-strength'

interface Props {
    password: string
    userInputs?: string[]
    className?: string
    // Minimum required zxcvbn score as configured in the password policy. When > 0 the indicator
    // renders a "minimum required: <level>" hint so the user can compare the current score against
    // the floor. 0 (the default) means the policy does not enforce a strength minimum and the hint
    // stays hidden. Values are clamped into the 0..4 range inside the indicator because the API
    // surfaces minStrengthScore as a plain number.
    minScore?: number
}

const MAX_SCORE = 4

function clampMinScore(value: number): 0 | 1 | 2 | 3 | 4 {
    if (!Number.isFinite(value) || value <= 0) return 0
    if (value >= MAX_SCORE) return MAX_SCORE
    return Math.round(value) as 0 | 1 | 2 | 3 | 4
}

// Per-segment colour classes, indexed 0..4. Both light and dark variants stay distinguishable in
// either theme; the empty-password case uses the same neutral fill for every segment.
const FILLED_COLORS: Record<0 | 1 | 2 | 3 | 4, string> = {
    0: 'bg-red-500 dark:bg-red-600',
    1: 'bg-orange-500 dark:bg-orange-600',
    2: 'bg-yellow-400 dark:bg-yellow-500',
    3: 'bg-lime-500 dark:bg-lime-600',
    4: 'bg-green-600 dark:bg-green-700',
}
const EMPTY_COLOR = 'bg-zinc-200 dark:bg-zinc-700'

const NEUTRAL: PasswordStrengthResult = {
    score: 0,
    crackTimeDisplay: '',
    warning: '',
    suggestions: [],
}

export default function PasswordStrengthIndicator({password, userInputs, className, minScore = 0}: Props) {
    const {t} = useTranslation('common')
    const [result, setResult] = useState<PasswordStrengthResult>(NEUTRAL)
    const effectiveMinScore = clampMinScore(minScore)

    useEffect(() => {
        if (!password) {
            setResult(NEUTRAL)
            return
        }

        let cancelled = false
        evaluatePassword(password, userInputs).then((next) => {
            if (!cancelled) {
                setResult(next)
            }
        })

        return () => {
            cancelled = true
        }
    }, [password, userInputs])

    const isActive = password.length > 0
    const hint = result.warning || result.suggestions[0] || ''
    const levelLabel = t(`passwordStrength.level.${result.score}`)

    return (
        <div
            className={`mt-2 ${className ?? ''}`}
            data-testid="password-strength-indicator"
            data-score={isActive ? result.score : 'empty'}
        >
            <div
                className="flex gap-1"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={isActive ? result.score : 0}
                aria-label={t('passwordStrength.label')}
                aria-valuetext={isActive ? levelLabel : t('passwordStrength.level.empty')}
            >
                {[0, 1, 2, 3, 4].map((segment) => {
                    const filled = isActive && segment <= result.score
                    const color = filled ? FILLED_COLORS[result.score] : EMPTY_COLOR
                    return (
                        <div
                            key={segment}
                            className={`h-2 flex-1 rounded ${color}`}
                            data-testid={`password-strength-segment-${segment}`}
                            data-filled={filled || null}
                        />
                    )
                })}
            </div>
            {effectiveMinScore > 0 && (
                <p
                    className="mt-1 text-xs text-zinc-500 dark:text-zinc-400"
                    data-testid="password-strength-minimum"
                    data-min-score={effectiveMinScore}
                >
                    {t('passwordStrength.minimum', {level: t(`passwordStrength.level.${effectiveMinScore}`)})}
                </p>
            )}
            {isActive && (
                <>
                    <p
                        className="mt-2 text-xs text-zinc-600 dark:text-zinc-300"
                        data-testid="password-strength-level"
                    >
                        {levelLabel}
                        {result.crackTimeDisplay && (
                            <>
                                {' · '}
                                <span data-testid="password-strength-crack-time">
                                    {t('passwordStrength.crackTime', {time: result.crackTimeDisplay})}
                                </span>
                            </>
                        )}
                    </p>
                    {hint && (
                        <p
                            className="mt-1 text-xs text-amber-700 dark:text-amber-300"
                            data-testid="password-strength-hint"
                        >
                            {hint}
                        </p>
                    )}
                </>
            )}
        </div>
    )
}
